/**
 * This mixin isolates all the data, methods, calculated properties
 * that are related to state of search filters.
 */

import Vue from 'vue'
import queryString from 'query-string'
import pickBy from 'lodash/pickBy'
import isEqual from 'lodash/isEqual'

import initCardsVueApp from '../cards-app'

const queryStringConfig = {
  arrayFormat: 'bracket',
  parseNumbers: true,
}

const defaultFilters = {
  search: '',
  code: '',
  type: [],
  budgetMin: null,
  budgetMax: null,
  pieces: [],
  bounds: null,
  surfaceMin: null,
  surfaceMax: null,
  am: [],
  to_promotion: false,

  // pagination
  sort: null,
  page: null,
  direction: null,
}

const filtersMixin = {
  data() {
    return {
      routes: {
        cards: '',
        coordinates: '',
      },
      budgetOptions: {
        acheter: [],
        louer: [],
      },
      action: '',
      cardsHtml: '',
      mapResults: [],
      showAdvancedFilters: false,

      ...(window.vueDefaultFiltersState || {}),

      filters: this.getStateSyncedWithUrl(window.location.search),
    }
  },
  watch: {
    action() {
      this.filters.budgetMin = null
      this.filters.budgetMax = null
    },
    cardsHtml() {
      const { cards } = this.$refs

      if (!cards) return

      Vue.nextTick(() => {
        initCardsVueApp(cards.children[0], {
          mounted: () => {
            const links = cards.querySelectorAll('.js-pagination a, .js-sort a')

            links.forEach(link => {
              const linkQuery = link.href && link.href.split('?')[1]

              link.addEventListener('click', e => {
                e.preventDefault()
                this.fetchCardsHtml(linkQuery)
                this.updatePageUrl(linkQuery)
                this.scrollTop()
              })
            })
            this.scroll.update()
          },
        })
      })
    },
    'filters.search': function() {
      this.filters.bounds = null
    },
    'filters.code': function() {
      this.filters.bounds = null
    },
  },
  computed: {
    budgetOptionsForAction() {
      const { action } = this

      if (!action || (action !== 'acheter' && action !== 'louer')) {
        return []
      }

      return this.budgetOptions[action]
    },
    budgetMinOptions() {
      if (!this.filters.budgetMax) {
        return this.budgetOptionsForAction
      }

      const numbers = this.budgetOptionsForAction.filter(
        number => number < this.filters.budgetMax
      )

      return numbers
    },
    budgetMaxOptions() {
      if (!this.filters.budgetMin) {
        return this.budgetOptionsForAction
      }

      const numbers = this.budgetOptionsForAction.filter(
        number => number > this.filters.budgetMin
      )

      return numbers
    },
    nonEmptyFilters() {
      return pickBy(this.filters, Boolean)
    },
    filtersAndAction() {
      if (!this.action) return this.nonEmptyFilters

      return { ...this.nonEmptyFilters, action: this.action }
    },
    searchString() {
      return queryString.stringify(this.nonEmptyFilters, queryStringConfig)
    },
    searchStringWithAction() {
      return queryString.stringify(this.filtersAndAction, queryStringConfig)
    },
    boundsObject() {
      return this.filters.bounds ? JSON.parse(this.filters.bounds) : null
    },
  },

  async mounted() {
    this.fetchCardsHtml()
    this.fetchCoordinatesJson()
    window.addEventListener('popstate', this.syncFiltersToUrl)
  },
  beforeDestroy() {
    window.removeEventListener('popstate', this.syncFiltersToUrl)
  },
  methods: {
    handleLocationOptionSelected(item) {
      if (item && item.code) {
        this.filters.code = item.code
      } else {
        this.filters.code = ''
      }
    },
    fetchCardsHtml(forceQueryString) {
      if (!this.routes.cards) return
      const finalQueryString = forceQueryString || this.searchStringWithAction

      fetch(`${this.routes.cards}?${finalQueryString}`)
        .then(res => res.text())
        .then(html => (this.cardsHtml = html))
    },
    fetchCoordinatesJson() {
      if (!this.routes.coordinates) return
      fetch(`${this.routes.coordinates}?${this.searchStringWithAction}`)
        .then(res => res.json())
        .then(
          json => (this.mapResults = json.filter(marker => marker.coordinates))
        )
    },
    updatePageUrl(forceQueryString) {
      if (forceQueryString) {
        const obj = queryString.parse(forceQueryString, queryStringConfig)

        delete obj.action
        forceQueryString = `?${queryString.stringify(obj, queryStringConfig)}`
      }
      const finalQueryString = forceQueryString || `?${this.searchString}`

      window.history.pushState(undefined, undefined, finalQueryString)
    },
    handleFiltersChange() {
      this.fetchCardsHtml()
      this.fetchCoordinatesJson()
      this.updatePageUrl()
      this.scrollTop()
    },
    handleBoundsChange(bounds) {
      if (bounds === null) return
      this.filters.bounds = JSON.stringify(bounds)
      this.filters.search = ''
      this.filters.code = ''
      this.handleFiltersChange()
    },
    clearFilters() {
      this.filters = { ...defaultFilters }
    },
    handleAdvancedOpenStateChange(value) {
      this.showAdvancedFilters = value
    },
    checkSurfaceMin() {
      if (this.filters.surfaceMin <= 0) {
        this.filters.surfaceMin = null
      } else if (
        this.filters.surfaceMax &&
        this.filters.surfaceMin > this.filters.surfaceMax
      ) {
        this.filters.surfaceMin = this.filters.surfaceMax
      }
    },
    checkSurfaceMax() {
      if (this.filters.surfaceMax <= 0) {
        this.filters.surfaceMax = null
      } else if (
        this.filters.surfaceMin &&
        this.filters.surfaceMax < this.filters.surfaceMin
      ) {
        this.filters.surfaceMax = this.filters.surfaceMin
      }
    },
    getStateSyncedWithUrl(url) {
      const urlState = queryString.parse(url, queryStringConfig)

      return { ...defaultFilters, ...urlState }
    },
    syncFiltersToUrl() {
      const newFilters = this.getStateSyncedWithUrl(window.location.search)

      if (!isEqual(this.filters, newFilters)) {
        this.filters = newFilters
        this.handleFiltersChange()
      }
    },
  },
}

export default filtersMixin
